import map from 'lodash/map'
import React, { memo, useMemo, VFC } from 'react'
import styled from 'styled-components'

import SVGGoogle from '@Assets/SVG/Google'
import { StarRating } from '@Components/StarRating'
import { ProductReview } from '@Generated/graphql'
import { up } from '@Globals/Style/breakpoints'
import { useTranslation } from '@Globals/Translations'

type ISingleProductReviewProps = {
  currentLocale?: string
  review: ProductReview
}

const ReviewWrapper = styled.article(({ theme }) => ({
  borderBottom: `1px solid ${theme.colors.greyLighter}`,
  marginBottom: theme.padding.tablet,
  paddingBottom: 5,
}))

const MetaWrapper = styled.div(({ theme }) => ({
  alignContent: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '.75rem',
  marginBottom: 5,

  '*': {
    color: theme.colors.grey,
  },

  [up('phablet')]: {
    fontSize: '.875rem',
  },
}))

const AuthorInfo = styled.div({
  lineHeight: 1.2,
})

const Header = styled.div({
  fontSize: '1rem',
  fontWeight: 700,
  textTransform: 'none',
})

const Text = styled.p({
  fontSize: '.875rem',

  span: {
    display: 'block',
  },
})

const TranslatedIndicator = styled.aside(({ theme }) => `
  font-size: .75rem;
  color: ${theme.colors.grey};
  font-style: italic;
`)

const GoogleLogo = styled(SVGGoogle)`
  width: .75rem;
  vertical-align: middle;
  margin-right: .4em;
  margin-top: -.2em;
`

export const SingleProductReview: VFC<ISingleProductReviewProps> = memo(({ currentLocale, review }): JSX.Element => {
  const { t } = useTranslation()

  // We don't want to just inject the data uncritically, but we DO want linebreaks. This is a fast and easy
  // way of doing the linebreaks without actually allowing HTML to end up here.
  const processedTextArray = useMemo(() => (review.localizedText || '')
    .trim()
    .replace(/\n+/g, '\n')
    .split('\n'), [review.localizedText])

  return (
    <ReviewWrapper>
      <MetaWrapper>
        <AuthorInfo>
          {t('products:BY_LABEL')}
          {' '}
          <b>{review.customerName}</b>
          {review.createdAt && (
            <>
              &nbsp;
              &nbsp;
              {new Date(review.createdAt).toLocaleDateString(currentLocale)}
            </>
          )}
        </AuthorInfo>
        <StarRating rating={review.rating || 0} />
      </MetaWrapper>
      <Header>{review.localizedTitle}</Header>
      <Text>{map(processedTextArray, line => <span key={line}>{line}</span>)}</Text>
      {review.locale !== currentLocale && (
        <TranslatedIndicator>
          <GoogleLogo />
          {t('products:TRANSLATED_BY_GOOGLE_LABEL')}
        </TranslatedIndicator>
      )}
    </ReviewWrapper>
  )
})
